import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { ScrollArea } from 'components/ui/scroll-area';
import { 
  Send, Wifi, WifiOff, Circle, MessageCircle, Users, ArrowLeft, Phone, Video
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const WS_URL = BACKEND_URL ? BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://') : (window.location.protocol === 'https:' ? 'wss://' : 'ws://') + window.location.host;

export default function RealtimeChat({ circleId, recipientId, onBack }) {
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [connected, setConnected] = useState(false);
  const [typing, setTyping] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [recipient, setRecipient] = useState(null);
  const [circle, setCircle] = useState(null);
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const retriesRef = useRef(0);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch initial data
  useEffect(() => {
    if (circleId) {
      fetchCircleMessages();
    } else if (recipientId) {
      fetchDirectMessages();
    }
    fetchOnlineUsers();
  }, [circleId, recipientId]);

  const fetchCircleMessages = async () => {
    try {
      const response = await api.get(`/ws/circle/${circleId}/messages`);
      setMessages(response.data.messages || []);
      setCircle(response.data.circle);
    } catch (error) {
      console.error('Failed to fetch circle messages:', error);
    }
  };

  const fetchDirectMessages = async () => {
    try {
      const response = await api.get(`/messages/${recipientId}`);
      setMessages(response.data.messages || []);
      setRecipient(response.data.other_user);
    } catch (error) {
      console.error('Failed to fetch direct messages:', error);
    }
  };

  const fetchOnlineUsers = async () => {
    try {
      const response = await api.get('/ws/online-users');
      setOnlineUsers(response.data.online_users || []);
    } catch (error) {
      console.error('Failed to fetch online users:', error);
    }
  };

  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    if (!token || wsRef.current?.readyState === WebSocket.OPEN) return;

    const ws = new WebSocket(`${WS_URL}/api/ws/chat/${token}`);
    wsRef.current = ws;

    ws.onopen = () => {
      retriesRef.current = 0;
      setConnected(true);
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      switch (data.type) {
        case 'connected':
          console.log('Connection confirmed');
          break;
        
        case 'new_message':
          // Direct message received
          if (!circleId && data.message.sender_id === recipientId) {
            setMessages(prev => [...prev, data.message]);
          }
          break;
        
        case 'circle_message':
          // Circle message received
          if (circleId && data.message.circle_id === circleId) {
            setMessages(prev => [...prev, data.message]);
          }
          break;
        
        case 'message_sent':
          // Confirmation of sent message
          setMessages(prev => [...prev, data.message]);
          break;
        
        case 'typing':
          if ((recipientId && data.user_id === recipientId) || 
              (circleId && data.circle_id === circleId && data.user_id !== user?.user_id)) {
            setTyping(data.user_name);
            // Clear typing after 3 seconds
            setTimeout(() => setTyping(null), 3000);
          }
          break;
        
        case 'message_read':
          // Update read status
          setMessages(prev => prev.map(msg => 
            msg.message_id === data.message_id ? { ...msg, read: true } : msg
          ));
          break;
        
        case 'pong':
          // Keep-alive response
          break;
        
        default:
          console.log('Unknown message type:', data.type);
      }
    };

    ws.onclose = () => {
      setConnected(false);
      if (retriesRef.current < 3) {
        retriesRef.current += 1;
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket();
        }, 3000 * retriesRef.current);
      }
    };

    ws.onerror = () => {
      setConnected(false);
    };

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      ws.close();
    };
  }, [token, circleId, recipientId, user?.user_id]);

  useEffect(() => {
    connectWebSocket();
    
    // Keep-alive ping every 30 seconds
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => {
      clearInterval(pingInterval);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connectWebSocket]);

  // Send message
  const sendMessage = () => {
    if (!newMessage.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    const messageData = circleId 
      ? { type: 'circle_message', circle_id: circleId, content: newMessage }
      : { type: 'direct_message', recipient_id: recipientId, content: newMessage };

    wsRef.current.send(JSON.stringify(messageData));
    setNewMessage('');
  };

  // Handle typing indicator
  const handleTyping = () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Send typing indicator
    const typingData = circleId
      ? { type: 'typing', circle_id: circleId }
      : { type: 'typing', recipient_id: recipientId };
    
    wsRef.current.send(JSON.stringify(typingData));

    // Set timeout to stop "typing" state
    typingTimeoutRef.current = setTimeout(() => {
      // Typing stopped
    }, 2000);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const isOnline = (userId) => onlineUsers.some(u => u.user_id === userId);

  return (
    <Card className="flex flex-col h-[600px] md:h-[700px]" data-testid="realtime-chat">
      {/* Header */}
      <CardHeader className="border-b py-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {onBack && (
              <Button variant="ghost" size="sm" onClick={onBack}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
            )}
            <div className="flex items-center gap-2">
              {circleId ? (
                <>
                  <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base">{circle?.name || 'Care Circle'}</CardTitle>
                    <p className="text-xs text-slate-500">{circle?.members?.length || 0} members</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="relative">
                    <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                      {recipient?.picture ? (
                        <img src={recipient.picture} alt="" className="w-full h-full rounded-full object-cover" />
                      ) : (
                        <span className="text-lg font-medium text-slate-600">
                          {recipient?.name?.charAt(0) || '?'}
                        </span>
                      )}
                    </div>
                    {recipientId && isOnline(recipientId) && (
                      <Circle className="absolute bottom-0 right-0 w-3 h-3 text-green-500 fill-green-500" />
                    )}
                  </div>
                  <div>
                    <CardTitle className="text-base">{recipient?.name || 'Chat'}</CardTitle>
                    <p className="text-xs text-slate-500">
                      {recipientId && isOnline(recipientId) ? 'Online' : 'Offline'}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={connected ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}>
              {connected ? <Wifi className="w-3 h-3 mr-1" /> : <WifiOff className="w-3 h-3 mr-1" />}
              {connected ? 'Live' : 'Connecting...'}
            </Badge>
          </div>
        </div>
      </CardHeader>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {messages.length === 0 ? (
            <div className="text-center py-8">
              <MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No messages yet</p>
              <p className="text-sm text-slate-400">Start the conversation!</p>
            </div>
          ) : (
            messages.map((msg, idx) => {
              const isOwn = msg.sender_id === user?.user_id;
              return (
                <div
                  key={msg.message_id || idx}
                  className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                  data-testid={`message-${msg.message_id || idx}`}
                >
                  <div className={`max-w-[75%] ${isOwn ? 'order-2' : 'order-1'}`}>
                    {!isOwn && (
                      <p className="text-xs text-slate-500 mb-1 ml-1">{msg.sender_name}</p>
                    )}
                    <div
                      className={`rounded-2xl px-4 py-2 ${
                        isOwn
                          ? 'bg-teal-500 text-white rounded-br-md'
                          : 'bg-slate-100 text-slate-900 rounded-bl-md'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                    <p className={`text-xs mt-1 ${isOwn ? 'text-right mr-1' : 'ml-1'} text-slate-400`}>
                      {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      {isOwn && msg.read && <span className="ml-1">✓✓</span>}
                    </p>
                  </div>
                </div>
              );
            })
          )}
          
          {/* Typing indicator */}
          {typing && (
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
              </div>
              <span>{typing} is typing...</span>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="border-t p-4">
        <div className="flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => {
              setNewMessage(e.target.value);
              handleTyping();
            }}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            className="flex-1"
            disabled={!connected}
            data-testid="chat-input"
          />
          <Button 
            onClick={sendMessage} 
            disabled={!connected || !newMessage.trim()}
            className="bg-teal-500 hover:bg-teal-600"
            data-testid="send-message-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        {!connected && (
          <p className="text-xs text-amber-600 mt-2">
            Connecting to chat server...
          </p>
        )}
      </div>
    </Card>
  );
}
